import { useState } from 'react'
import { Check } from 'lucide-react'
import type { NtBlock, NtSchedule, NtTerm } from '../../core/types'
import { setArtifactBlock } from './model'
import { ArtifactList } from './ArtifactOptions'
import { ErrorState } from '../../ui/States'

/**
 * A student's week, and the one decision in it that is theirs: which of their
 * two courses they make their open-house artifact in. That also decides where
 * they are Friday morning. Each choice shows that course's artifact options,
 * so the decision is "what would I rather make", not "which teacher".
 */
export function StudentSchedule({ term, schedule, me }: {
  term: NtTerm
  schedule: NtSchedule | null | undefined
  me: { email: string; name: string }
}) {
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  if (schedule === null) return null
  if (schedule === undefined) {
    return (
      <section className="card" style={{ marginTop: 'var(--s-3)' }}>
        <h2 className="chk__title">My week</h2>
        <p className="opt__hint" style={{ margin: '4px 0 0' }}>
          Your courses appear here once placement has run. You will then choose which one you make your
          open-house artifact in.
        </p>
      </section>
    )
  }

  const choose = (b: NtBlock) => async () => {
    setBusy(true); setErr(null)
    try { await setArtifactBlock(schedule.id, b, me.name) }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)) }
    finally { setBusy(false) }
  }

  const blocks = (['AM', 'PM'] as const).filter((b) => (b === 'AM' ? schedule.amCourseId : schedule.pmCourseId))
  const label = (b: NtBlock) => (b === 'AM' ? schedule.amTitle : schedule.pmTitle) ?? 'Your course'
  const teacher = (b: NtBlock) => (b === 'AM' ? schedule.amTeacherName : schedule.pmTeacherName)
  const room = (b: NtBlock) => (b === 'AM' ? schedule.amRoom : schedule.pmRoom)
  const optionsOf = (b: NtBlock) => (b === 'AM' ? schedule.amArtifacts : schedule.pmArtifacts)
  const chosen = schedule.artifactBlock

  return (
    <section className="card" style={{ marginTop: 'var(--s-3)' }}>
      <h2 className="chk__title">My week · {term.weekLabel}</h2>

      {schedule.independent ? (
        <p style={{ fontSize: 'var(--t-sm)', margin: 'var(--s-2) 0 0' }}>
          You are on an independent project: <strong>{schedule.independentTitle}</strong>. What it produces is
          your artifact for the open house.
        </p>
      ) : (
        <>
          <table className="sched">
            <tbody>
              <tr><th>Tuesday – Thursday</th><td>
                {blocks.map((b) => (
                  <div key={b}><strong>{b === 'AM' ? 'Morning' : 'Afternoon'}:</strong> {label(b)}
                    <span className="work__sub"> · {teacher(b)}{room(b) ? ` · ${room(b)}` : ''}</span></div>
                ))}
              </td></tr>
              <tr><th>Friday 8:15–9:45</th><td>
                {schedule.noArtifact ? <>With <strong>{label('AM')}</strong>, your morning credit recovery class</>
                  : chosen ? <>Artifact &amp; open-house prep in <strong>{label(chosen)}</strong></> : <em>Choose your artifact course below</em>}
              </td></tr>
            </tbody>
          </table>
          {schedule.noArtifact ? (
            <p className="opt__hint" style={{ marginTop: 'var(--s-3)' }}>
              You&apos;re in credit recovery all week, so you don&apos;t make an open-house artifact.
            </p>
          ) : <>

          <h3 style={{ fontSize: 'var(--t-md)', margin: 'var(--s-4) 0 var(--s-1)' }}>Where will you make your artifact?</h3>
          <p className="opt__hint" style={{ marginTop: 0 }}>
            Everyone makes one artifact for the open house, in one of your two courses, not both. Pick the one
            whose options you would rather do. {chosen && 'You can change your mind. The office can see and change it too.'}
          </p>
          {err && <ErrorState body={`Not saved. ${err}`} />}
          <div className="sched__choices" role="radiogroup" aria-label="Artifact course">
            {blocks.filter((b) => !(b === 'AM' ? schedule.amCreditRecovery : schedule.pmCreditRecovery)).map((b) => (
              <button key={b} role="radio" aria-checked={chosen === b} disabled={busy}
                className={`sched__choice${chosen === b ? ' is-on' : ''}`} onClick={choose(b)}>
                <div className="sched__choice-head">
                  <span>{b === 'AM' ? 'Morning' : 'Afternoon'} · <strong>{label(b)}</strong></span>
                  {chosen === b && <span className="pill pill--good"><Check size={12} aria-hidden="true" /> Your choice</span>}
                </div>
                <ArtifactList options={optionsOf(b)} compact />
              </button>
            ))}
          </div>
          </>}
        </>
      )}
    </section>
  )
}
